import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  X,
  Settings,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import api from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

interface ImplementModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
  onSuccess: () => void;
}

export default function ImplementModal({ isOpen, onClose, formId, onSuccess }: ImplementModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const implementMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        implementationNotes: notes,
        implementedById: user?.id,
      };

      const response = await api.post(`/access-forms/${formId}/implement`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessForm', formId] });
      queryClient.invalidateQueries({ queryKey: ['accessForms'] });
      resetForm();
      onSuccess();
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to mark as implemented');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    implementMutation.mutate();
  };

  const resetForm = () => {
    setNotes('');
    setSubmitError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2 text-emerald-600">
            <Settings className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Mark as Implemented</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Error Alert */}
          {submitError && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <p className="text-destructive">{submitError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" />
                <p className="font-medium">Access Provisioned</p>
              </div>
              <p className="text-sm text-emerald-600 mt-1">
                Confirm that the requested access has been provisioned in all systems.
              </p>
            </div>

            {/* Implementation Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Implementation Notes (Optional)</Label>
              <textarea
                id="notes"
                className="w-full min-h-[120px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Document what was provisioned, any special configurations, or notes for future reference..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="p-3 bg-muted rounded-lg">
              <h4 className="font-medium text-sm mb-2">Checklist</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>✓ All requested systems have been configured</li>
                <li>✓ User accounts have been created</li>
                <li>✓ Access permissions have been set</li>
                <li>✓ Employee has been notified</li>
              </ul>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={implementMutation.isPending}
            loading={implementMutation.isPending}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Mark as Implemented
          </Button>
        </div>
      </div>
    </div>
  );
}
